import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import styled, { css, withTheme } from 'styled-components';
import { Row, Column } from '@lux/components';
import Sticky from 'react-sticky-el';

import IconChevronLeft from '@lux/components/src/assets/svgs/chevron-left.svg';
import IconChevronRight from '@lux/components/src/assets/svgs/chevron-right.svg';

const StyledGallery = styled.div`
  width: 100%;
`;

const FeaturedImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const Chevron = styled.div`
  display: inline-flex;
  cursor: pointer;
`;

const ChevronLeft = styled(Chevron)`
  padding-right: ${p => p.theme.spacing(3)};
  ${p =>
    p.isFirst &&
    css`
      visibility: hidden;
    `};
`;

const ChevronRight = styled(Chevron)`
  padding-left: ${p => p.theme.spacing(3)};
  ${p =>
    p.isLast &&
    css`
      visibility: hidden;
    `};
`;

const ImageStrip = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Thumbnails = styled.div`
  display: flex;
  min-width: 264px;
  justify-content: center;
`;

const Thumbnail = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 ${p => p.theme.spacing(0.5)};
  ${p =>
    p.active &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colours.link};
    `};
  padding: ${p => p.theme.spacing(0.5)};
  border: none;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;

  transition: 0.25s all ease-in-out;

  ${p =>
    !p.active &&
    css`
      &:hover {
        box-shadow: 0 0 0 1px ${p.theme.colours.grey_600};
      }
    `};
`;

const PhoneImageGallery = props => {
  const { imageGallery, limit, title, theme } = props;
  let imageGroups = [];

  //grouping the images based on the limit
  for (let i = 0; i < imageGallery?.images?.length; i += limit) {
    const chunk = imageGallery?.images
      .filter(({ image }) => image && image.childImageSharp)
      .slice(i, i + limit);
    imageGroups.push(chunk);
  }

  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedImageGroup, setSelectedImageGroup] = useState(0);

  const decreaseIndex = () => {
    if (selectedImage === 0 && selectedImageGroup > 0) {
      setSelectedImageGroup(selectedImageGroup - 1);
      setSelectedImage(2);
    } else if (selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const increaseIndex = () => {
    if (
      selectedImage === limit - 1 &&
      selectedImageGroup < imageGroups.length - 1
    ) {
      setSelectedImageGroup(selectedImageGroup + 1);
      setSelectedImage(0);
    } else if (selectedImage < images.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
  };

  useEffect(() => {
    setImages(imageGroups[selectedImageGroup]);
  }, [selectedImageGroup]);

  useEffect(() => {
    setImages(imageGroups[0]);
    setSelectedImage(0);
    setSelectedImageGroup(0);
  }, [imageGallery]);

  return (
    <Sticky
      boundaryElement={`[class*='StyledGridBoundary']`}
      hideOnBoundaryHit={false}
      topOffset={-72}
      stickyStyle={{ padding: `${theme.spacing(9)} 0` }}
      positionRecheckInterval={1}
    >
      <StyledGallery data-testid={`gallery-${imageGallery.hexCode}`}>
        <Row center="sm" middle="sm">
          <Column>
            <FeaturedImage>
              {images[selectedImage] && (
                <Img
                  image={
                    images[selectedImage].image?.childImageSharp
                      ?.gatsbyImageData
                  }
                  alt={title}
                  loading="lazy"
                  style={{ width: 380, height: 380 }}
                />
              )}
            </FeaturedImage>
          </Column>
        </Row>
        <Row center="sm" middle="sm">
          <Column>
            <ImageStrip>
              {images.length > 0 && (
                <ChevronLeft
                  isFirst={selectedImage === 0 && selectedImageGroup === 0}
                  onClick={() => decreaseIndex()}
                  data-testid="chevron-prev"
                >
                  <IconChevronLeft width={24} height={24} />
                </ChevronLeft>
              )}
              <Thumbnails data-testid="thumbnails">
                {images &&
                  images.map(({ image }, key) => (
                    <Thumbnail
                      key={key}
                      active={selectedImage === key}
                      onClick={() => setSelectedImage(key)}
                      data-testid={`thumbnail-${key}`}
                    >
                      <Img
                        image={image.childImageSharp.gatsbyImageData}
                        loading="lazy"
                        style={{ width: 72, height: 72 }}
                      />
                    </Thumbnail>
                  ))}
              </Thumbnails>
              {images.length > 0 && (
                <ChevronRight
                  isLast={
                    selectedImage === images.length - 1 &&
                    selectedImageGroup >= imageGroups.length - 1
                  }
                  onClick={() => increaseIndex()}
                  data-testid="chevron-next"
                >
                  <IconChevronRight width={24} height={24} />
                </ChevronRight>
              )}
            </ImageStrip>
          </Column>
        </Row>
      </StyledGallery>
    </Sticky>
  );
};

PhoneImageGallery.defaultProps = {
  imageGallery: [],
  limit: 3,
  title: ''
};

PhoneImageGallery.propTypes = {
  /** Image gallery object */
  imageGallery: PropTypes.shape({
    name: PropTypes.string,
    hexCode: PropTypes.string,
    images: PropTypes.array
  }),
  /** Limit the number of thumbnails to show */
  limit: PropTypes.number,
  /** Title of the image gallery */
  title: PropTypes.string
};

export default withTheme(PhoneImageGallery);
